
import { useState } from 'react';

export default function StoryPage() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const milestones = [
    {
      year: '2018',
      title: 'The Beginning',
      description: 'Founded with a passion for creating the perfect mango dessert using traditional recipes and modern techniques.',
      icon: 'ri-seedling-line'
    },
    {
      year: '2019',
      title: 'First Recipe Perfected',
      description: 'After countless trials, we perfected our signature mango pudding recipe with only 105 calories.',
      icon: 'ri-flask-line'
    },
    {
      year: '2020',
      title: 'Natural Ingredients Focus',
      description: 'Committed to using only natural mango pulp and fresh ingredients, eliminating all artificial additives.',
      icon: 'ri-leaf-line'
    },
    {
      year: '2021',
      title: 'Award Recognition',
      description: 'Received the Premium Dessert Excellence Award for outstanding taste and quality.',
      icon: 'ri-award-line'
    },
    {
      year: '2022',
      title: 'Sustainable Practices',
      description: 'Implemented eco-friendly packaging and sustainable sourcing practices for all ingredients.',
      icon: 'ri-earth-line'
    },
    {
      year: '2024',
      title: 'Premium Collection Launch',
      description: 'Expanded our product line with premium varieties and seasonal flavors to delight our customers.',
      icon: 'ri-star-line'
    }
  ];

  const values = [
    {
      title: 'Quality First',
      description: 'We never compromise on quality, using only the finest mango pulp and natural ingredients in every product.',
      icon: 'ri-shield-check-line',
      color: 'from-rose-400 via-pink-400 to-orange-400'
    },
    {
      title: 'Natural & Healthy',
      description: 'Our commitment to natural ingredients means no artificial preservatives, colors, or flavors.',
      icon: 'ri-heart-pulse-line',
      color: 'from-green-400 to-emerald-400'
    },
    {
      title: 'Innovation',
      description: 'Continuously improving our recipes and processes to deliver the best possible taste experience.',
      icon: 'ri-lightbulb-line',
      color: 'from-blue-400 to-cyan-400'
    },
    {
      title: 'Sustainability',
      description: 'Committed to environmentally responsible practices in sourcing, production, and packaging.',
      icon: 'ri-recycle-line',
      color: 'from-yellow-400 to-orange-400'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-amber-50 to-orange-50">
      {/* Free Shipping Banner */}
      <section className="bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-3">
            <i className="ri-truck-line text-2xl"></i>
            <span className="text-lg font-semibold">
              FREE SHIPPING on orders over $50!
            </span>
            <i className="ri-gift-line text-2xl"></i>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <nav className="bg-gradient-to-r from-rose-100/95 via-pink-100/95 to-orange-100/95 backdrop-blur-md shadow-xl sticky top-0 z-50 border-b border-rose-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/e97325bca996f1ce3b54a2c00c06237a/b38163c3c482e773cae655e9f9113361.png"
                alt="Mango Treasure Logo"
                className="h-14 w-auto mr-4 drop-shadow-sm"
              />
              <span
                className="text-3xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-orange-600 bg-clip-text text-transparent"
                style={{ fontFamily: '"Pacifico", serif' }}
              >
                Mango Treasure
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/"
                >
                  Home
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/products"
                >
                  Shop Products
                </a>
                <a
                  className="text-rose-700 hover:text-rose-900 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/story"
                >
                  Our Story
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/contact"
                >
                  Contact
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/orders"
                >
                  Track Orders
                </a>
                <a
                  className="bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white px-6 py-3 rounded-full hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg flex items-center space-x-2 font-semibold"
                  href="/cart"
                >
                  <i className="ri-shopping-cart-line text-lg"></i>
                  <span>Cart</span>
                </a>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="bg-white/90 backdrop-blur-sm hover:bg-white text-rose-600 p-3 rounded-lg shadow-lg transition-all duration-300 cursor-pointer border border-rose-100"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 bg-white/95 backdrop-blur-md rounded-lg mt-2 shadow-xl border border-rose-200/50">
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/products"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Shop Products
                </a>
                <a
                  className="text-rose-700 hover:text-rose-900 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/story"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Our Story
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/orders"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Track Orders
                </a>
                <a
                  className="bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white px-6 py-3 rounded-full hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 cursor-pointer shadow-lg flex items-center space-x-2 font-semibold justify-center mt-4"
                  href="/cart"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-shopping-cart-line text-lg"></i>
                  <span>Cart</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        className="relative py-32 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('https://public.readdy.ai/ai/img_res/a3f1c6eb610c34597f6256c520d96e3b.jpg')`
        }}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight drop-shadow-lg">Our Story</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed drop-shadow-md">
            From a simple passion for mangoes to creating the world's finest mango pudding
          </p>
        </div>
      </section>

      {/* Story Introduction */}
      <section className="py-20 bg-white/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent mb-6">The Mango Treasure Journey</h2>
              <p className="text-lg text-rose-600 mb-6 leading-relaxed">
                It all started with a simple love for mangoes and a dream to create the perfect dessert. Our founder, Maria Rodriguez, grew up in a family that owned mango orchards in tropical regions, where she learned to appreciate the true essence of this magnificent fruit.
              </p>
              <p className="text-lg text-rose-600 mb-6 leading-relaxed">
                After years of experimenting with traditional recipes and modern culinary techniques, Maria discovered the perfect balance of flavors, textures, and nutritional value that would become our signature Mango Treasure Pudding.
              </p>
              <p className="text-lg text-rose-600 mb-8 leading-relaxed">
                Today, we continue to honor that original vision by using only the finest natural ingredients and maintaining the highest standards of quality in every product we create.
              </p>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-rose-200/80 via-pink-200/80 to-orange-200/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-rose-100/50">
                <img 
                  alt="Founder Maria Rodriguez with fresh mangoes" 
                  className="w-full h-80 object-cover rounded-2xl shadow-lg object-top"
                  src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20female%20chef%20founder%20holding%20fresh%20mangoes%20in%20modern%20kitchen%2C%20warm%20lighting%2C%20confident%20smile%2C%20culinary%20expertise%2C%20food%20entrepreneur&width=500&height=400&seq=2&orientation=landscape"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white px-6 py-3 rounded-full font-bold shadow-lg">
                Since 2018
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gradient-to-r from-rose-50/80 via-pink-50/80 to-orange-50/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent mb-4">Our Journey Through Time</h2>
            <p className="text-xl text-rose-600 max-w-3xl mx-auto">
              From humble beginnings to becoming a premium dessert brand
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-rose-400 via-pink-400 to-orange-400 rounded-full"></div>
            
            {milestones.map((milestone, index) => (
              <div key={index} className={`relative flex items-center mb-12 ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                <div className={`w-full max-w-md ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}>
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-rose-100/50">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <i className={`${milestone.icon} text-white text-xl`}></i>
                      </div>
                      <div>
                        <span className="text-2xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent">{milestone.year}</span>
                        <h3 className="text-lg font-semibold text-rose-800">{milestone.title}</h3>
                      </div>
                    </div>
                    <p className="text-rose-600">{milestone.description}</p>
                  </div>
                </div>
                
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-full border-4 border-white shadow-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent mb-4">Our Core Values</h2>
            <p className="text-xl text-rose-600 max-w-3xl mx-auto">
              The principles that guide everything we do at Mango Treasure
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center group">
                <div className={`w-20 h-20 bg-gradient-to-r ${value.color} rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <i className={`${value.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-rose-800 mb-3">{value.title}</h3>
                <p className="text-rose-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-r from-rose-100/80 via-pink-100/80 to-orange-100/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent mb-4">Meet Our Team</h2>
            <p className="text-xl text-rose-600 max-w-3xl mx-auto">
              The passionate people behind Mango Treasure's success
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-rose-100/50">
              <img 
                alt="Maria Rodriguez - Founder & CEO" 
                className="w-32 h-32 object-cover rounded-full mx-auto mb-4 object-top shadow-lg"
                src="https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20confident%20female%20CEO%20founder%2C%20warm%20smile%2C%20business%20attire%2C%20leadership%20portrait%2C%20food%20industry%20executive&width=300&height=300&seq=3&orientation=squarish"
              />
              <h3 className="text-xl font-semibold text-rose-800 mb-2">Maria Rodriguez</h3>
              <p className="text-rose-600 mb-4 font-medium">Founder & CEO</p>
              <p className="text-rose-600 text-sm">Passionate about creating healthy, delicious desserts that bring joy to people's lives.</p>
            </div>
            
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-rose-100/50">
              <img 
                alt="Chef David Kim - Head of Product Development" 
                className="w-32 h-32 object-cover rounded-full mx-auto mb-4 object-top shadow-lg"
                src="https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20male%20chef%20in%20chef%20coat%2C%20friendly%20expression%2C%20culinary%20expert%2C%20food%20development%20specialist%2C%20kitchen%20background&width=300&height=300&seq=4&orientation=squarish"
              />
              <h3 className="text-xl font-semibold text-rose-800 mb-2">Chef David Kim</h3>
              <p className="text-rose-600 mb-4 font-medium">Head of Product Development</p>
              <p className="text-rose-600 text-sm">Culinary expert with 15 years of experience in creating innovative dessert recipes.</p>
            </div>
            
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-200 border border-rose-100/50">
              <img 
                alt="Sarah Chen - Quality Assurance Manager" 
                className="w-32 h-32 object-cover rounded-full mx-auto mb-4 object-top shadow-lg"
                src="https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20female%20quality%20assurance%20manager%2C%20confident%20smile%2C%20laboratory%20coat%2C%20food%20safety%20expert%2C%20professional%20portrait&width=300&height=300&seq=5&orientation=squarish"
              />
              <h3 className="text-xl font-semibold text-rose-800 mb-2">Sarah Chen</h3>
              <p className="text-rose-600 mb-4 font-medium">Quality Assurance Manager</p>
              <p className="text-rose-600 text-sm">Ensures every product meets our highest standards for quality, safety, and taste.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-rose-600 via-pink-600 to-orange-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6 drop-shadow-lg">Join Our Story</h2>
          <p className="text-xl text-rose-100 mb-8 max-w-2xl mx-auto drop-shadow-md">
            Become part of the Mango Treasure family and experience the passion we put into every product
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a className="bg-white text-rose-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-rose-50 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg transform hover:scale-105" href="/products">
              Try Our Products
            </a>
            <a className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-rose-600 transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer" href="/contact">
              Get in Touch
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-rose-900 via-pink-900 to-orange-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="https://static.readdy.ai/image/e97325bca996f1ce3b54a2c00c06237a/b38163c3c482e773cae655e9f9113361.png" 
                  alt="Mango Treasure Logo" 
                  className="h-10 w-auto mr-3 drop-shadow-sm"
                />
                <h3 className="text-2xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>Mango Treasure</h3>
              </div>
              <p className="text-rose-200 mb-4">
                Premium mango pudding crafted with love and natural ingredients for the perfect dessert experience.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a className="text-rose-200 hover:text-white transition-colors cursor-pointer" href="/">Home</a></li>
                <li><a className="text-rose-200 hover:text-white transition-colors cursor-pointer" href="/products">Products</a></li>
                <li><a className="text-rose-200 hover:text-white transition-colors cursor-pointer" href="/story">Our Story</a></li>
                <li><a className="text-rose-200 hover:text-white transition-colors cursor-pointer" href="/contact">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Customer Service</h4>
              <ul className="space-y-2">
                <li><a className="text-rose-200 hover:text-white transition-colors cursor-pointer" href="/orders">Track Orders</a></li>
                <li><a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">FAQ</a></li>
                <li><a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">Shipping Info</a></li>
                <li><a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">Returns</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Connect With Us</h4>
              <div className="flex space-x-4 mb-4">
                <a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-2xl"></i>
                </a>
                <a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-2xl"></i>
                </a>
                <a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-fill text-2xl"></i>
                </a>
              </div>
              <p className="text-rose-200 text-sm">Follow us for updates and special offers</p>
            </div>
          </div>
          <div className="border-t border-rose-800 mt-8 pt-8 text-center">
            <p className="text-rose-200">
              © 2024 Mango Treasure. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="text-rose-200 hover:text-white ml-2 transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
